// src/services/apiClient.js
import axios from "axios";

// In-memory tokens (separate for public api vs admin api)
let tokens = { api: null, admin: null };

// Refresh state & queues per client
let isRefreshing = { api: false, admin: false };
let refreshSubscribers = { api: [], admin: [] };

// ⏱️ Auto-renew timers per client
let autoRenewTimers = { api: null, admin: null };

export const setAuthToken = (token, isAdmin = false) => {
  const key = isAdmin ? "admin" : "api";
  tokens[key] = token || null;
};

export const hydrateTokensFromStorage = () => {
  const apiToken = localStorage.getItem("apiAuthToken");
  const adminToken = localStorage.getItem("adminAuthToken");
  if (apiToken) tokens.api = apiToken;
  if (adminToken) tokens.admin = adminToken;
};

const onRefreshed = (token, isAdmin) => {
  const key = isAdmin ? "admin" : "api";
  refreshSubscribers[key].forEach((cb) => cb(token));
  refreshSubscribers[key] = [];
};

const addSubscriber = (cb, isAdmin) => {
  const key = isAdmin ? "admin" : "api";
  refreshSubscribers[key].push(cb);
};

// --- Axios instances ---
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://api.connectummah.com/api",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

const adminApiClient = axios.create({
  baseURL: process.env.REACT_APP_ADMIN_API_BASE_URL || "https://adminapi.connectummah.com/api",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

// 🔁 Shared re-login (used by interceptor & auto-renew)
const rawRelogin = async (client, isAdmin) => {
  const USERNAME = process.env.REACT_APP_USERNAME;
  const PASSWORD = process.env.REACT_APP_PASSWORD;
  const TENANT_ID = process.env.REACT_APP_TENANT_ID || "";
  // const USERNAME ='communityadmin@gmail.com';
  // const PASSWORD = 'adminpass1!';
  // const TENANT_ID = 4;

  const { data } = await client.post(
    "/Auth/LoginMultiTenantWithTenantId",
    { Username: USERNAME, Password: PASSWORD, Tenant_Id: TENANT_ID },
    { _skipAuth: true }
  );
  const newToken = data?.token;
  if (!newToken) throw new Error("No token from re-auth.");
  const key = isAdmin ? "admin" : "api";
  tokens[key] = newToken;
  localStorage.setItem(isAdmin ? "adminAuthToken" : "apiAuthToken", newToken);
  onRefreshed(newToken, isAdmin);
  return newToken;
};

// 📌 Public helper to renew token on demand (used by scheduler)
export const renewToken = async (isAdmin = false) => {
  const key = isAdmin ? "admin" : "api";
  try {
    if (isRefreshing[key]) return tokens[key]; // already doing it
    isRefreshing[key] = true;
    const client = isAdmin ? adminApiClient : apiClient;
    const newToken = await rawRelogin(client, isAdmin);
    return newToken;
  } catch (e) {
    console.error("Scheduled token renew failed:", e?.response?.data || e?.message);
    throw e;
  } finally {
    isRefreshing[key] = false;
  }
};

// 🕒 Start/stop 50-min auto-renew
const MINUTES = 50;
const INTERVAL_MS = MINUTES * 60 * 1000;

// Optional: tiny jitter (±30s) so multiple tabs don’t all renew at the exact same ms
const jitter = () => Math.floor((Math.random() * 60 - 30) * 1000);

const canRenewNow = () =>
  typeof document === "undefined" || document.visibilityState === "visible";

const scheduleOne = (isAdmin) => {
  const key = isAdmin ? "admin" : "api";
  clearInterval(autoRenewTimers[key]);
  autoRenewTimers[key] = setInterval(async () => {
    if (!tokens[key]) return;                 // nothing to renew
    if (!canRenewNow()) return;               // skip if tab hidden
    if (isRefreshing[key]) return;            // skip if already refreshing
    try {
      await renewToken(isAdmin);
      // console.log(`[auto-renew] ${key} renewed`);
    } catch (_) {
      // keep silent; interceptor will still handle 401s later
    }
  }, INTERVAL_MS + jitter());
};

export const startAutoRenewAll = () => {
  scheduleOne(false); // api
  scheduleOne(true);  // admin
};

export const stopAutoRenewAll = () => {
  clearInterval(autoRenewTimers.api);
  clearInterval(autoRenewTimers.admin);
  autoRenewTimers.api = null;
  autoRenewTimers.admin = null;
};

// --- Attach interceptors ---
const attachInterceptors = (client, isAdmin = false) => {
  const key = isAdmin ? "admin" : "api";

  client.interceptors.request.use(
    (config) => {
      if (config && config._skipAuth) return config;
      const token = tokens[key];
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error && error.config ? error.config : undefined;
      if (!originalRequest || originalRequest._skipAuth) {
        return Promise.reject(error);
      }

      if (error?.response?.status !== 401) return Promise.reject(error);
      if (originalRequest._retry) return Promise.reject(error);

      if (isRefreshing[key]) {
        return new Promise((resolve, reject) => {
          addSubscriber((newToken) => {
            if (!newToken) return reject(error);
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(client(originalRequest));
          }, isAdmin);
        });
      }

      originalRequest._retry = true;
      isRefreshing[key] = true;
      try {
        const newToken = await rawRelogin(client, isAdmin);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);
      } catch (refreshErr) {
        console.error(
          "Re-authentication failed.",
          refreshErr?.response?.data || refreshErr?.message
        );
        tokens[key] = null;
        localStorage.removeItem(isAdmin ? "adminAuthToken" : "apiAuthToken");
        onRefreshed(null, isAdmin); // fail queued
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing[key] = false;
      }
    }
  );
};

attachInterceptors(apiClient, false);
attachInterceptors(adminApiClient, true);

export { apiClient, adminApiClient };
